import { useState, useEffect, useCallback } from "react";
import { DollarSign } from "lucide-react";
import { MonthlyBarChart, ProductPieChart } from "../components/dashboard/CommissionCharts";
import { CommissionKPIs, CommissionRow, CommissionCard, BrokerRankItem, EditCommissionModal } from "../components/dashboard/CommissionParts";
import CommissionFilters from "../components/dashboard/CommissionFilters";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

var CHART_COLORS = ["#3B82F6", "#F26A4B", "#10b981", "#f59e0b", "#8b5cf6"];
var PRODUCT_LABELS = { consorcio: "Consorcio", seguro: "Seguro", carta_contemplada: "Carta Contemplada" };

export default function CommissionsPage() {
  var auth = useAuth();
  var isAdmin = auth.user ? auth.user.role === "admin" : false;
  var isTeamLeader = auth.user ? auth.user.role === "team_leader" : false;
  var canFilterBroker = isAdmin || isTeamLeader;

  var commissionsState = useState([]);
  var totalState = useState(0);
  var loadingState = useState(true);
  var pageState = useState(1);
  var analyticsState = useState(null);
  var filterStatusState = useState("");
  var filterProductState = useState("");
  var filterBrokerState = useState("");
  var editingState = useState(null);
  var brokersState = useState([]);
  var exportingState = useState(false);

  var fetchCommissions = useCallback(async function() {
    loadingState[1](true);
    try {
      var params = new URLSearchParams();
      if (filterStatusState[0]) params.append("status", filterStatusState[0]);
      if (filterProductState[0]) params.append("product_type", filterProductState[0]);
      if (filterBrokerState[0]) params.append("broker_id", filterBrokerState[0]);
      params.append("page", pageState[0]);
      params.append("limit", "20");
      var res = await api.get("/commissions?" + params.toString());
      commissionsState[1](res.data.commissions || []);
      totalState[1](res.data.total || 0);
    } catch(err) { console.error(err); } finally { loadingState[1](false); }
  }, [filterStatusState[0], filterProductState[0], filterBrokerState[0], pageState[0]]);

  var fetchAnalytics = useCallback(async function() {
    try { var res = await api.get("/commissions/analytics"); analyticsState[1](res.data); } catch(err) { console.error(err); }
  }, []);

  useEffect(function() { fetchCommissions(); }, [fetchCommissions]);
  useEffect(function() { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(function() {
    if (canFilterBroker) { api.get("/clients/brokers-list").then(function(r) { brokersState[1](r.data || []); }).catch(function() {}); }
  }, [canFilterBroker]);

  async function handleExport() {
    exportingState[1](true);
    try {
      var params = new URLSearchParams();
      if (filterStatusState[0]) params.append("status", filterStatusState[0]);
      if (filterProductState[0]) params.append("product_type", filterProductState[0]);
      if (filterBrokerState[0]) params.append("broker_id", filterBrokerState[0]);
      var res = await api.get("/commissions/export?" + params.toString(), { responseType: "blob" });
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var a = document.createElement("a"); a.href = url; a.download = "comissoes_export.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch(err) { console.error(err); } finally { exportingState[1](false); }
  }

  function handleSaveEdit() { editingState[1](null); fetchCommissions(); fetchAnalytics(); }

  var totalPages = Math.ceil(totalState[0] / 20);
  var totals = analyticsState[0] ? analyticsState[0].totals : {};
  if (!totals) totals = {};
  var totalPago = totals.pago ? totals.pago.total : 0;
  var totalPendente = totals.pendente ? totals.pendente.total : 0;
  var totalTravada = totals.travada ? totals.travada.total : 0;
  var totalProcessando = totals.processando ? totals.processando.total : 0;
  var totalGeral = totalPago + totalPendente + totalTravada + totalProcessando;

  var pieData = [];
  var byProduct = analyticsState[0] ? analyticsState[0].by_product : null;
  if (byProduct) { for (var i = 0; i < byProduct.length; i++) { pieData.push({ name: PRODUCT_LABELS[byProduct[i].product_type] || byProduct[i].product_type, value: byProduct[i].total }); } }

  var brokerRankItems = [];
  var byBroker = analyticsState[0] ? analyticsState[0].by_broker : null;
  if (isAdmin && byBroker && byBroker.length > 0) {
    var brokerMax = byBroker[0].total || 1;
    for (var j = 0; j < byBroker.length; j++) { brokerRankItems.push(<BrokerRankItem key={j} b={byBroker[j]} idx={j} maxVal={brokerMax} color={CHART_COLORS[j % CHART_COLORS.length]} />); }
  }

  var comms = commissionsState[0];
  var rows = []; var cards = [];
  for (var k = 0; k < comms.length; k++) {
    rows.push(<CommissionRow key={comms[k].id} c={comms[k]} isAdmin={isAdmin} onEdit={editingState[1]} />);
    cards.push(<CommissionCard key={comms[k].id} c={comms[k]} isAdmin={isAdmin} onEdit={editingState[1]} />);
  }

  return (
    <AppShell activePage="comissoes" title="Comissoes">
      <div data-testid="commissions-page">
        {editingState[0] && <EditCommissionModal commission={editingState[0]} onClose={function() { editingState[1](null); }} onSave={handleSaveEdit} />}
        <CommissionKPIs totalGeral={totalGeral} totalPago={totalPago} totalPendente={totalPendente + totalProcessando} totalTravada={totalTravada} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
          <div className="lg:col-span-2 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="chart-monthly">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Comissoes por Periodo</h3>
            <p className="text-xs text-gray-400 mb-4">Ultimos 6 meses por status</p>
            <MonthlyBarChart data={analyticsState[0] ? analyticsState[0].monthly : null} />
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="chart-product">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Por Produto</h3>
            <p className="text-xs text-gray-400 mb-4">Distribuicao total</p>
            <ProductPieChart data={pieData} />
          </div>
        </div>
        {brokerRankItems.length > 0 && (
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm mb-6" data-testid="broker-ranking">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-4">Ranking por Corretor</h3>
            <div className="space-y-3">{brokerRankItems}</div>
          </div>
        )}
        <CommissionFilters
          filterStatus={filterStatusState[0]} setFilterStatus={filterStatusState[1]}
          filterProduct={filterProductState[0]} setFilterProduct={filterProductState[1]}
          filterBroker={filterBrokerState[0]} setFilterBroker={filterBrokerState[1]}
          canFilterBroker={canFilterBroker} brokers={brokersState[0]}
          onExport={handleExport} exporting={exportingState[0]}
          totalCount={totalState[0]} setPage={pageState[1]}
        />
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden" data-testid="commission-table-container">
          {loadingState[0] ? (
            <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
          ) : comms.length === 0 ? (
            <div className="text-center py-16"><DollarSign className="w-12 h-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-500 font-medium">Nenhuma comissao encontrada</p></div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Lead</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Produto</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Valor</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                    {isAdmin && <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Corretor</th>}
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Data</th>
                    {isAdmin && <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Acoes</th>}
                  </tr></thead>
                  <tbody>{rows}</tbody>
                </table>
              </div>
              <div className="md:hidden divide-y divide-gray-50">{cards}</div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Pagina {pageState[0]} de {totalPages}</p>
                  <div className="flex gap-1">
                    <button disabled={pageState[0] <= 1} onClick={function() { pageState[1](pageState[0] - 1); }} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30" data-testid="commission-prev-page">Anterior</button>
                    <button disabled={pageState[0] >= totalPages} onClick={function() { pageState[1](pageState[0] + 1); }} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30" data-testid="commission-next-page">Proxima</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
